<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { expectErrorMessage } from "@core/utils/expectErrorMessage.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./ResendConfirmationCode.translations.json" with { type: "json" };

	let {
		/* eslint-disable prefer-const */
		searchParams,
		/* eslint-enable prefer-const */
		email = $bindable(searchParams?.get("email") ?? undefined),
	}: { searchParams?: URLSearchParams; email?: string } = $props();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const isDesktop = createReactiveBreakpoint(">md");
	const resendCode = createPromiseWithStatus(Auth.resendConfirmationCode.bind(Auth, tenant.locale));

	const error = $derived(
		resendCode.value.status === "error" &&
			expectErrorMessage(resendCode.value.error, tenant.locale, {}, translate("resendCodeError")),
	);

	async function handleResetCode(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		const formData = new FormData(form);
		const email = formData.get("email") as string;
		try {
			await resendCode.call(email);
		} finally {
			setTimeout(() => {
				form.reset();
				resendCode.value.status = "initial";
			}, 60000);
		}
	}
</script>

<Form class="flex w-full flex-col items-stretch gap-2 mt-5 md:mt-10" method="dialog" onsubmit={handleResetCode}>
	<Input name="email" value={email} type="email" required placeholder="E-mail" />

	<Button
		type="submit"
		loading={resendCode.value.status === "loading"}
		disabled={resendCode.value.status === "loading"}
		variant={ButtonVariant.Dark}
		size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
		class="mt-4"
	>
		<Formatted text={translate(resendCode.value.status === "success" ? "resendButton" : "submitButton")} />
	</Button>
	{#if error}
		<p class="text-sm text-fcrerror mt-2">
			<Formatted text={error} />
		</p>
	{:else if resendCode.value.status === "success"}
		<p class="text-sm text-fcrgray mt-2">
			<Formatted text={translate("resendSuccess")} />
			<button
				onclick={async (): Promise<void> => {
					await Auth.openAuthenticationModal(AuthenticationModalKind.ConfirmSignUp, { email });
				}}
				type="button"
				class="underline"
			>
				<Formatted text={translate("confirmAccount")} />
			</button>
		</p>
	{/if}
</Form>
